import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { FileText, X } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TranslateFileJob } from '../types';

interface FileJobProgressProps {
  job: TranslateFileJob;
  cancelling: boolean;
  onCancel: () => void;
}

export const FileJobProgress = memo(function FileJobProgress({
  job,
  cancelling,
  onCancel,
}: FileJobProgressProps) {
  const { t } = useTranslation();
  const { completedChunks, totalChunks } = job.progress;
  const determinate = job.status === 'running' && totalChunks > 0;

  return (
    <Box className="space-y-2 border-b border-gray-200 bg-blue-50 px-4 py-3 dark:border-slate-700 dark:bg-blue-950/30">
      <Box className="flex min-w-0 flex-wrap items-center gap-2">
        <FileText size={18} className="shrink-0 text-blue-600 dark:text-blue-400" />
        <Typography
          variant="body2"
          className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-white"
        >
          {job.source.fileName}
        </Typography>
        <Button
          size="small"
          color="inherit"
          disabled={cancelling}
          onClick={onCancel}
          startIcon={<X size={16} />}
          className="text-gray-700 dark:text-slate-200"
          sx={{ minHeight: 44 }}
        >
          {cancelling ? t('translate.job.cancelling') : t('translate.job.cancel')}
        </Button>
      </Box>
      <LinearProgress
        variant={determinate ? 'determinate' : 'indeterminate'}
        value={determinate ? Math.min(100, (completedChunks / totalChunks) * 100) : undefined}
        aria-label={t('translate.job.queued')}
      />
      <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
        {determinate
          ? t('translate.job.progress', { completed: completedChunks, total: totalChunks })
          : t('translate.job.queued')}
      </Typography>
    </Box>
  );
});
